import React from "react";

const CARD_STEP = 292; // card width + gap

/**
 * Horizontal card rail shared by Certifications and Events — one component so
 * the two sections can never drift apart visually again.
 */
export default function CardRail({ items, onImageClick, autoScroll = false }) {
    const railRef = React.useRef(null);
    const [paused, setPaused] = React.useState(false);

    React.useEffect(() => {
        if (!autoScroll || paused) return undefined;

        // On touch there is no hover to pause on, so an auto-advancing rail
        // just fights the finger. Let touch users drive it themselves.
        const coarse =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(hover: none)").matches;
        if (coarse) return undefined;

        const id = setInterval(() => {
            const rail = railRef.current;
            if (!rail) return;
            const maxScroll = rail.scrollWidth - rail.clientWidth;
            if (maxScroll <= 0) return;
            if (rail.scrollLeft >= maxScroll - 4) {
                rail.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                rail.scrollBy({ left: CARD_STEP, behavior: "smooth" });
            }
        }, 4000);

        return () => clearInterval(id);
    }, [autoScroll, paused]);

    return (
        // Full-bleed on phones (cancels the shell's gutter) so the rail runs to
        // the screen edge and reads as scrollable; inset again from sm up.
        <div
            className="rail-mask -mx-6 sm:mx-0"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <ul ref={railRef} className="rail px-6 sm:px-1">
                {items.map(item => (
                    <li
                        key={item.id}
                        className="w-[248px] shrink-0 sm:w-[276px]"
                    >
                        <article className="card group overflow-hidden">
                            <div className="flex h-[132px] items-center justify-center border-b border-line bg-canvas-deep/40 p-5">
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    onClick={
                                        onImageClick
                                            ? () =>
                                                  onImageClick(
                                                      item.image,
                                                      item.title
                                                  )
                                            : undefined
                                    }
                                    onError={e => {
                                        e.target.style.visibility = "hidden";
                                    }}
                                    className={`max-h-full max-w-[140px] object-contain transition-transform
                                                duration-500 ease-soft group-hover:scale-[1.06] ${
                                                    onImageClick
                                                        ? "cursor-zoom-in"
                                                        : ""
                                                }`}
                                />
                            </div>

                            <div className="flex flex-1 flex-col gap-3 p-5">
                                <h3 className="text-[15px] font-medium leading-snug text-ink">
                                    {item.title}
                                </h3>
                                {item.meta && (
                                    <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-faint">
                                        {item.meta}
                                    </p>
                                )}
                                {item.link && (
                                    <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-auto inline-flex w-fit items-center gap-1.5 pt-1
                                                   font-mono text-[11px] uppercase tracking-[0.12em]
                                                   text-ink-dim transition-colors duration-300 hover:text-accent"
                                    >
                                        {item.linkLabel || "Verify"}
                                        <span aria-hidden="true">↗</span>
                                    </a>
                                )}
                            </div>
                        </article>
                    </li>
                ))}
            </ul>
        </div>
    );
}
